import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { EpSelfEnrolmentNavService } from '../ep-self-enrolment-nav.service';
import { EpSelfEnrolmentFormService } from '../ep-self-enrolment-form-service';
import { TypeService } from '../../../../../../services/type/type-service';
import { finalize, switchMap, take } from 'rxjs/operators';
import { BaseButtonSizeEnum } from '../../../../../shared/components/base-button/base-button-size.enum';
import { EpSelfEnrolmentFormFactory } from '../forms/ep-self-enrolment-form-factory';
import { EmployersAPI } from '../../../../../../api/employers-api';
import { BehaviorSubject, forkJoin, Observable, of, throwError } from 'rxjs';
import { ToastService } from '../../../../../../services/toast-service';
import { CustomError } from '../../../../../../models/shared/custom-error';
import { Router } from '@angular/router';
import { Routes } from '../../../../../../routes/routes';
import { AttachmentApi } from '../../../../../../api/attachment-api';
import { EpSelfEnrolmentResponse } from '../../../../../../models/employers/ep-self-enrolment-response';
import { SelfEnrolmentLogo } from '../../../../../../models/employers/self-enrolment-logo';

@Component({
  selector: 'app-ep-self-enrolment-contact-personnel',
  templateUrl: './ep-self-enrolment-contact-personnel.component.html',
  styleUrls: ['./ep-self-enrolment-contact-personnel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EpSelfEnrolmentContactPersonnelComponent {
  private attachmentApi = inject(AttachmentApi);
  private employersApi = inject(EmployersAPI);
  private formService = inject(EpSelfEnrolmentFormService);
  private navService = inject(EpSelfEnrolmentNavService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private typeService = inject(TypeService);

  public form = this.formService.form;

  public primaryContactForm = this.form.controls.primaryContact;
  public additonalContactForms = this.form.controls.additionalContacts;

  public formSubmitted = false;

  public formErrorMessages = {
    noSigningAuthority: $localize`The employer must have a Signing Authority. Give signing authority to at least 1 contact.`
  };

  private _isLoading = new BehaviorSubject(false);
  public isLoading$ = this._isLoading.asObservable();

  public countryCodeDropdowns$ = this.typeService.countryCodes$;

  public addContact(): void {
    this.additonalContactForms.push(EpSelfEnrolmentFormFactory.buildAdditionalContactFormGroup());
  }

  public removeContact(index: number): void {
    this.additonalContactForms.removeAt(index);
  }

  public goBack(): void {
    this.navService.goBack();
  }

  public submit(): void {
    this.formSubmitted = true;
    const { about, headOfficeAddress, mailingAddress, logo } = this.form.controls;
    const mailingAddressValid = mailingAddress.valid || mailingAddress.controls.sameAsHeadOffice.value;

    if (about.valid && headOfficeAddress.valid && mailingAddressValid && logo.valid) {
      this._isLoading.next(true);
      const request = this.formService.buildRequest();
      this.employersApi
        .epSelfEnrol(request)
        .pipe(
          take(1),
          switchMap(resp => this.uploadLogoIfPresent(resp)),
          finalize(() => this._isLoading.next(false))
        )
        .subscribe({
          next: () => {
            this.toastService.publishSuccessMessage(
              $localize`The Employer Registration was Submitted`,
              $localize`An invite will be sent to the primary contact provided during registration.`
            );
            this.navService.canDeactivate = true;
            this.router.navigate([Routes.signIn()]).then();
          },
          error: (err: CustomError) => {
            this.toastService.publishError(err);
          }
        });
    }
  }

  private uploadLogoIfPresent(resp: EpSelfEnrolmentResponse): Observable<any> {
    const logoFile = this.form.controls.logo.value?.[0];
    if (!logoFile) return of(null);

    const logos = [resp?.employerLogo, resp?.submissionLogo];
    const uploadTargets = logos.filter(
      (logo): logo is SelfEnrolmentLogo => !!logo?.id && !!logo?.attachment?.links?.[0]?.presignedUrl
    );
    if (uploadTargets.length !== logos.length) {
      return throwError(() => new CustomError(new Error($localize`The logo could not be uploaded.`)));
    }

    return forkJoin(
      uploadTargets.map(logo =>
        this.attachmentApi.uploadFile(logoFile, String(logo.id), logo.attachment.links[0].presignedUrl)
      )
    );
  }

  protected readonly BaseButtonSizeEnum = BaseButtonSizeEnum;
}
